import type { FC, ReactNode } from 'react'
import type { Categoria } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import AppImage from '@/components/generals/app-image'

interface CategoryCardProps {
    categoria: Categoria
    compact?: boolean
}

const CategoryCard: FC<CategoryCardProps> = ({ categoria, compact = false }): ReactNode => {
    const total = categoria.total_productos

    return (
        <Link
            href={`/catalogo/${categoria.slug}`}
            className={cn(
                'group relative flex flex-col justify-end overflow-hidden rounded-sm border border-ivory/10 bg-graphite transition-colors duration-500 hover:border-brass/50',
                compact ? 'aspect-[4/3] sm:aspect-[3/4]' : 'aspect-[3/4]'
            )}
        >
            <AppImage
                src={categoria.imagen}
                alt={categoria.imagen_alt}
                sizes='(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw'
                className='transition-transform duration-700 ease-out group-hover:scale-105 motion-reduce:transition-none'
            />

            <div aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-black via-black/60 to-black/5' />

            <div className='relative flex flex-col gap-3 p-6 sm:p-7'>
                <p className='text-[0.62rem] font-semibold uppercase tracking-[0.28em] text-brass'>
                    {total} {total === 1 ? 'pieza' : 'piezas'}
                </p>

                <h3 className='font-heading text-3xl leading-none font-medium uppercase tracking-wide text-foreground'>
                    <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>
                </h3>

                {categoria.descripcion && !compact && (
                    <p className='line-clamp-3 text-sm leading-relaxed text-ivory/75' data-mdlfy-cms={cms('categorias', categoria.id, 'descripcion')}>
                        {categoria.descripcion}
                    </p>
                )}

                <span className='mt-1 inline-flex items-center gap-2 text-[0.66rem] font-semibold uppercase tracking-[0.22em] text-ivory transition-colors group-hover:text-brass'>
                    Explorar
                    <ArrowRight aria-hidden='true' className='size-3.5 transition-transform duration-300 group-hover:translate-x-1' />
                </span>
            </div>
        </Link>
    )
}

export default CategoryCard
